import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import {
  adminModulesApi,
  AdminTechAsset,
  CoreExtensionsImportCounts,
  CoreExtensionsUploadBody,
  DataMappingMappingKey,
  DataMappingSchemaLayer,
  DataMappingState,
  MappingImportCounts,
  SchemaMappingUploadBody,
  SourceSchemaImportCounts,
  SourceSchemaUploadBody,
} from '../api/adminModules';

type LastResult =
  | { kind: 'schema';  layer: DataMappingSchemaLayer; counts: SourceSchemaImportCounts }
  | { kind: 'mapping'; key:   DataMappingMappingKey;  counts: MappingImportCounts }
  | { kind: 'core-ext'; counts: CoreExtensionsImportCounts }
  | null;

// Per-client Data Mapping admin UI. Renders four upload cards (System schema,
// Staging schema, System->Staging mapping, Staging->ClientVerse mapping) for a
// given client. Mounted as a tab on the AdminClientDetail page; the global
// Modules > Data Mapping page only manages ClientVerse Core.
export default function ClientDataMapping({ clientId }: { clientId: string }) {
  const { getToken } = useAuth();
  const [state, setState] = useState<DataMappingState | null>(null);
  const [techInventory, setTechInventory] = useState<AdminTechAsset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<LastResult>(null);

  const load = async () => {
    try {
      const t = await getToken();
      const [st, ti] = await Promise.all([
        adminModulesApi.getDataMappingState(t, clientId),
        adminModulesApi.listTechInventory(t, clientId),
      ]);
      setState(st);
      setTechInventory(ti);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [clientId]);

  return (
    <div>
      <p style={{ color: 'var(--mi-muted)', marginTop: 0, marginBottom: 16, fontSize: 13 }}>
        Upload the schema and mapping JSON files that drive this client&rsquo;s Data Mapping module. Each artifact is scoped to this client &mdash; re-uploading replaces the previous version. ClientVerse Core stays global and is managed under <em>Admin &rsaquo; Modules &rsaquo; Data Mapping</em>.
      </p>

      {error && <div style={errBox}>{error}</div>}

      <ClientSystemsPanel assets={techInventory} />

      <h3 style={sectionHeading}>Schemas</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
        <SchemaUploadCard
          title="System Schema"
          accent="var(--mi-navy)"
          description="Source system structure (e.g. InterAction)."
          uploadedAt={state?.system_uploaded_at ?? null}
          schemaIdStr={state?.system_schema_id_str ?? null}
          version={state?.system_schema_version ?? null}
          objects={state?.system_objects ?? 0}
          fields={state?.system_fields ?? 0}
          onUpload={async (body) => {
            const t = await getToken();
            const counts = await adminModulesApi.uploadSchema(t, clientId, 'system', body);
            setLastResult({ kind: 'schema', layer: 'system', counts });
            await load();
          }}
          onDownload={async () => {
            const t = await getToken();
            const body = await adminModulesApi.exportSchema(t, clientId, 'system');
            downloadJson(`system-schema-${clientId}.json`, body);
          }}
          onError={setError}
        />
        <SchemaUploadCard
          title="Staging Schema"
          accent="var(--mi-steel)"
          description="Transformed staging structure."
          uploadedAt={state?.staging_uploaded_at ?? null}
          schemaIdStr={state?.staging_schema_id_str ?? null}
          version={null}
          objects={state?.staging_objects ?? 0}
          fields={state?.staging_fields ?? 0}
          onUpload={async (body) => {
            const t = await getToken();
            const counts = await adminModulesApi.uploadSchema(t, clientId, 'staging', body);
            setLastResult({ kind: 'schema', layer: 'staging', counts });
            await load();
          }}
          onDownload={async () => {
            const t = await getToken();
            const body = await adminModulesApi.exportSchema(t, clientId, 'staging');
            downloadJson(`staging-schema-${clientId}.json`, body);
          }}
          onError={setError}
        />
      </div>

      <h3 style={sectionHeading}>Mappings</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
        <MappingUploadCard
          title="System → Staging"
          accent="var(--mi-steel)"
          description="Object/field mappings moving data from System into Staging."
          uploadedAt={state?.sys_to_stg_uploaded_at ?? null}
          objectMappings={state?.sys_to_stg_object_mappings ?? 0}
          fieldMappings={state?.sys_to_stg_field_mappings ?? 0}
          onUpload={async (body) => {
            const t = await getToken();
            const counts = await adminModulesApi.uploadMapping(t, clientId, 'system-to-staging', body);
            setLastResult({ kind: 'mapping', key: 'system-to-staging', counts });
            await load();
          }}
          onDownload={async () => {
            const t = await getToken();
            const body = await adminModulesApi.exportMapping(t, clientId, 'system-to-staging');
            downloadJson(`system-to-staging-mapping-${clientId}.json`, body);
          }}
          onError={setError}
        />
        <MappingUploadCard
          title="Staging → ClientVerse"
          accent="var(--mi-purple)"
          description="Object/field mappings from Staging into ClientVerse Core (Salesforce)."
          uploadedAt={state?.stg_to_cv_uploaded_at ?? null}
          objectMappings={state?.stg_to_cv_object_mappings ?? 0}
          fieldMappings={state?.stg_to_cv_field_mappings ?? 0}
          onUpload={async (body) => {
            const t = await getToken();
            const counts = await adminModulesApi.uploadMapping(t, clientId, 'staging-to-clientverse', body);
            setLastResult({ kind: 'mapping', key: 'staging-to-clientverse', counts });
            await load();
          }}
          onDownload={async () => {
            const t = await getToken();
            const body = await adminModulesApi.exportMapping(t, clientId, 'staging-to-clientverse');
            downloadJson(`staging-to-clientverse-mapping-${clientId}.json`, body);
          }}
          onError={setError}
        />
      </div>

      <h3 style={sectionHeading}>ClientVerse Core Extensions</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
        <CoreExtensionsCard
          uploadedAt={state?.core_ext_uploaded_at ?? null}
          schemaIdStr={state?.core_ext_schema_id ?? null}
          version={state?.core_ext_schema_version ?? null}
          clientOnlyObjects={state?.core_ext_objects_client_only ?? 0}
          fieldsExtendingCore={state?.core_ext_fields_extending_core ?? 0}
          fieldsClientOnly={state?.core_ext_fields_client_only ?? 0}
          onUpload={async (body) => {
            const t = await getToken();
            const counts = await adminModulesApi.uploadCoreExtensions(t, clientId, body);
            setLastResult({ kind: 'core-ext', counts });
            await load();
          }}
          onDownload={async () => {
            const t = await getToken();
            const body = await adminModulesApi.exportCoreExtensions(t, clientId);
            downloadJson(`clientverse-core-extensions-${clientId}.json`, body);
          }}
          onError={setError}
        />
      </div>

      {lastResult && <ResultModal result={lastResult} onClose={() => setLastResult(null)} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Card: Schema upload
// -----------------------------------------------------------------------------
function SchemaUploadCard({ title, accent, description, uploadedAt, schemaIdStr, version, objects, fields, onUpload, onDownload, onError }: {
  title: string;
  accent: string;
  description: string;
  uploadedAt: string | null;
  schemaIdStr: string | null;
  version: string | null;
  objects: number;
  fields: number;
  onUpload: (body: SourceSchemaUploadBody) => Promise<void>;
  onDownload: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const canDownload = !!uploadedAt;

  const handleDownload = async () => {
    setDownloading(true);
    try { await onDownload(); }
    catch (e) { onError(e instanceof Error ? e.message : String(e)); }
    finally { setDownloading(false); }
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed || !Array.isArray(parsed.objects)) {
        throw new Error('Invalid file: expected { "objects": [...] } at the top level.');
      }
      await onUpload(parsed as SourceSchemaUploadBody);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" style={{ borderTop: `3px solid ${accent}` }}>
      <h4 style={cardTitle}>{title}</h4>
      <p style={cardSub}>{description}</p>
      <div style={metaGrid}>
        <Stat label="Objects"     value={objects} />
        <Stat label="Fields"      value={fields} />
        <Stat label="Last upload" value={uploadedAt ? new Date(uploadedAt).toLocaleString() : 'never'} valueIsText />
      </div>
      {(schemaIdStr || version) && (
        <div style={{ fontSize: 12, color: 'var(--mi-muted)', marginTop: 8 }}>
          {schemaIdStr && <><code>{schemaIdStr}</code>{version ? ` · v${version}` : ''}</>}
        </div>
      )}
      <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => fileInput.current?.click()} disabled={busy} className="btn-primary">
          {busy ? 'Uploading…' : 'Upload JSON'}
        </button>
        <button
          onClick={handleDownload}
          disabled={!canDownload || downloading}
          style={secondaryBtn}
          title={canDownload ? 'Download the current JSON' : 'Nothing to download — upload a schema first'}
        >
          {downloading ? 'Downloading…' : 'Download JSON'}
        </button>
        <input ref={fileInput} type="file" accept="application/json,.json" onChange={onFile} style={{ display: 'none' }} />
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Card: Mapping upload
// -----------------------------------------------------------------------------
function MappingUploadCard({ title, accent, description, uploadedAt, objectMappings, fieldMappings, onUpload, onDownload, onError }: {
  title: string;
  accent: string;
  description: string;
  uploadedAt: string | null;
  objectMappings: number;
  fieldMappings: number;
  onUpload: (body: SchemaMappingUploadBody) => Promise<void>;
  onDownload: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const canDownload = !!uploadedAt;

  const handleDownload = async () => {
    setDownloading(true);
    try { await onDownload(); }
    catch (e) { onError(e instanceof Error ? e.message : String(e)); }
    finally { setDownloading(false); }
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed || !Array.isArray(parsed.object_mappings)) {
        throw new Error('Invalid file: expected { "object_mappings": [...] } at the top level.');
      }
      await onUpload(parsed as SchemaMappingUploadBody);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" style={{ borderTop: `3px solid ${accent}` }}>
      <h4 style={cardTitle}>{title}</h4>
      <p style={cardSub}>{description}</p>
      <div style={metaGrid}>
        <Stat label="Object mappings" value={objectMappings} />
        <Stat label="Field mappings"  value={fieldMappings} />
        <Stat label="Last upload"     value={uploadedAt ? new Date(uploadedAt).toLocaleString() : 'never'} valueIsText />
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => fileInput.current?.click()} disabled={busy} className="btn-primary">
          {busy ? 'Uploading…' : 'Upload JSON'}
        </button>
        <button
          onClick={handleDownload}
          disabled={!canDownload || downloading}
          style={secondaryBtn}
          title={canDownload ? 'Download the current JSON' : 'Nothing to download — upload a mapping first'}
        >
          {downloading ? 'Downloading…' : 'Download JSON'}
        </button>
        <input ref={fileInput} type="file" accept="application/json,.json" onChange={onFile} style={{ display: 'none' }} />
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Card: ClientVerse Core Extensions upload
// -----------------------------------------------------------------------------
function CoreExtensionsCard({ uploadedAt, schemaIdStr, version, clientOnlyObjects, fieldsExtendingCore, fieldsClientOnly, onUpload, onDownload, onError }: {
  uploadedAt: string | null;
  schemaIdStr: string | null;
  version: string | null;
  clientOnlyObjects: number;
  fieldsExtendingCore: number;
  fieldsClientOnly: number;
  onUpload: (body: CoreExtensionsUploadBody) => Promise<void>;
  onDownload: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const canDownload = !!uploadedAt;

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed || !Array.isArray(parsed.objects)) {
        throw new Error('Invalid file: expected { "objects": [...] } at the top level.');
      }
      await onUpload(parsed as CoreExtensionsUploadBody);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try { await onDownload(); }
    catch (e) { onError(e instanceof Error ? e.message : String(e)); }
    finally { setDownloading(false); }
  };

  return (
    <section className="card" style={{ borderTop: '3px solid var(--mi-green)' }}>
      <h4 style={cardTitle}>Client Custom Objects &amp; Fields</h4>
      <p style={cardSub}>Objects and fields this client has on top of ClientVerse Core (e.g. custom fields on standard SF objects, client-only objects not in the managed package).</p>
      <div style={metaGrid}>
        <Stat label="Client-only objects"      value={clientOnlyObjects} />
        <Stat label="Fields extending core"    value={fieldsExtendingCore} />
        <Stat label="Fields on client objects" value={fieldsClientOnly} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6, marginTop: 6, fontSize: 12, color: 'var(--mi-muted)' }}>
        <div>Last upload: {uploadedAt ? new Date(uploadedAt).toLocaleString() : 'never'}</div>
        {(schemaIdStr || version) && (
          <div>{schemaIdStr && <code>{schemaIdStr}</code>}{version ? ` · v${version}` : ''}</div>
        )}
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => fileInput.current?.click()} disabled={busy} className="btn-primary">
          {busy ? 'Uploading…' : 'Upload JSON'}
        </button>
        <button
          onClick={handleDownload}
          disabled={!canDownload || downloading}
          style={secondaryBtn}
          title={canDownload ? 'Download the current JSON' : 'Nothing to download yet'}
        >
          {downloading ? 'Downloading…' : 'Download JSON'}
        </button>
        <input ref={fileInput} type="file" accept="application/json,.json" onChange={onFile} style={{ display: 'none' }} />
      </div>
    </section>
  );
}

// Read-only summary of what systems this client runs, sourced from the
// Tech Inventory module. Helps the admin see at a glance which schemas /
// mappings should apply before touching any upload buttons below.
function ClientSystemsPanel({ assets }: { assets: AdminTechAsset[] | null }) {
  const byCategory = useMemo(() => {
    const g = new Map<string, AdminTechAsset[]>();
    for (const a of assets ?? []) {
      let arr = g.get(a.category);
      if (!arr) { arr = []; g.set(a.category, arr); }
      arr.push(a);
    }
    return g;
  }, [assets]);

  if (assets === null) return null;

  const mvpCount = assets.filter((a) => a.phase === 'MVP').length;
  const postCount = assets.filter((a) => a.phase === 'Post-MVP').length;
  if (assets.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 20, padding: 12, borderLeft: '3px solid var(--mi-muted, #999)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <h4 style={{ ...cardTitle, margin: 0 }}>Client Systems</h4>
          <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>
            No systems recorded in Tech Inventory yet.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 20, padding: 12, borderLeft: '3px solid var(--mi-navy)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
        <h4 style={{ ...cardTitle, margin: 0 }}>Client Systems</h4>
        <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>
          {mvpCount} MVP{postCount ? ` · ${postCount} Post-MVP` : ''} / {assets.length} total &middot; from Tech Inventory
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
        {Array.from(byCategory.entries()).map(([category, items]) => (
          <div key={category} style={{ fontSize: 12 }}>
            <div style={{ color: 'var(--mi-muted)', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10, marginBottom: 4 }}>
              {category}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {items.map((a) => {
                const label = [a.vendor, a.version].filter(Boolean).join(' ') || a.name || '(unnamed)';
                // MVP = filled navy dot, Post-MVP = filled muted dot, none = hollow grey.
                const dot = a.phase === 'MVP' ? '●' : a.phase === 'Post-MVP' ? '●' : '○';
                const color = a.phase === 'MVP' ? 'var(--mi-navy)'
                            : a.phase === 'Post-MVP' ? '#888'
                            : '#bbb';
                return (
                  <div key={a.id} style={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, color,
                  }}>
                    <span title={a.phase ?? 'no phase'}>{dot}</span>
                    <span>{label}</span>
                    {a.phase === 'Post-MVP' && (
                      <span style={{ fontSize: 10, color: '#999', marginLeft: 'auto' }}>Post-MVP</span>
                    )}
                    {((a.data_supplied?.length ?? 0) > 0 || (a.staging_tables?.length ?? 0) > 0) && (
                      <div style={{ flexBasis: '100%', fontSize: 11, color: 'var(--mi-muted)', paddingLeft: 16 }}>
                        {[
                          (a.data_supplied ?? []).join(', '),
                          (a.staging_tables?.length ?? 0) > 0 ? `${a.staging_tables.length} staging table${a.staging_tables.length === 1 ? '' : 's'}` : '',
                        ].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, valueIsText }: { label: string; value: number | string; valueIsText?: boolean }) {
  return (
    <div style={{ background: 'var(--mi-bg)', padding: 10, borderRadius: 6, border: '1px solid var(--mi-border)' }}>
      <div style={{ fontSize: 11, color: 'var(--mi-muted)' }}>{label}</div>
      <div style={{ fontSize: valueIsText ? 13 : 20, fontWeight: 700, color: 'var(--mi-navy)', marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

function ResultModal({ result, onClose }: { result: NonNullable<LastResult>; onClose: () => void }) {
  const title =
    result.kind === 'schema'   ? `Loaded ${result.layer} schema` :
    result.kind === 'mapping'  ? `Loaded ${result.key} mapping`  :
    'Loaded ClientVerse Core Extensions';
  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, marginBottom: 12, color: 'var(--mi-green)' }}>{title}</h3>
        {result.kind === 'schema' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, fontSize: 14 }}>
            <div style={{ color: 'var(--mi-muted)' }}>Objects loaded</div>
            <div>{result.counts.objects_loaded}</div>
            <div style={{ color: 'var(--mi-muted)' }}>Fields loaded</div>
            <div>{result.counts.fields_loaded}</div>
          </div>
        )}
        {result.kind === 'mapping' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, fontSize: 14 }}>
            <div style={{ color: 'var(--mi-muted)' }}>Object mappings loaded</div>
            <div>{result.counts.object_mappings_loaded}</div>
            <div style={{ color: 'var(--mi-muted)' }}>Field mappings loaded</div>
            <div>{result.counts.field_mappings_loaded}</div>
          </div>
        )}
        {result.kind === 'core-ext' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, fontSize: 14 }}>
            <div style={{ color: 'var(--mi-muted)' }}>Client-only objects</div>
            <div>{result.counts.objects_client_only}</div>
            <div style={{ color: 'var(--mi-muted)' }}>Fields extending core objects</div>
            <div>{result.counts.fields_extending_core}</div>
            <div style={{ color: 'var(--mi-muted)' }}>Fields on client objects</div>
            <div>{result.counts.fields_client_only}</div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} className="btn-primary">Done</button>
        </div>
      </div>
    </div>
  );
}

// Trigger a browser download of an object serialized as pretty-printed JSON.
function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// -----------------------------------------------------------------------------
const errBox: React.CSSProperties = { background: '#fee', color: '#900', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 14 };
const secondaryBtn: React.CSSProperties = {
  background: 'transparent', border: '1px solid var(--mi-border)',
  color: 'var(--mi-navy)', padding: '6px 12px', borderRadius: 6,
  cursor: 'pointer', fontSize: 13, fontWeight: 600,
};
const sectionHeading: React.CSSProperties = { color: 'var(--mi-navy)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 0, marginBottom: 8 };
const cardTitle: React.CSSProperties = { margin: 0, fontSize: 15, color: 'var(--mi-navy)' };
const cardSub:   React.CSSProperties = { marginTop: 4, marginBottom: 12, fontSize: 12, color: 'var(--mi-muted)' };
const metaGrid:  React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 };
const modalBackdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalBox: React.CSSProperties = {
  background: '#fff', borderRadius: 8, padding: 20, maxWidth: 540, width: '90%',
  maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
};
